import axios from 'axios'
import { throttle } from 'throttle-debounce'

import getListingBasePath from 'Pods/Shared/Utils/getListingBasePath'
import getLocaleParam from 'Pods/Shared/Utils/getLocaleParam'
import localizeBackendUrl from 'Pods/Shared/Utils/localizeBackendUrl'

import queryClient from 'Utils/queryClient'

import buildSearchParams from '../buildSearchParams'
import { ASSET_TYPE } from '../constants'
import { searchPageStore } from '../index'
import {
  getFreeModelsBasePath, getFreePrintBasePath, getModelsBasePath, getPrintBasePath,
} from '../listingBasePaths'
import normalizeMeta from '../normalizeMeta'

// Matches the listing controller's DEFAULT_PER_PAGE; omitted from params when unchanged so the
// cache key (and URL) stays clean for the common case.
export const DEFAULT_PER_PAGE = 120
// Mirrors ListingPage's react-query cache window: re-applying a seen filter combo (or paging
// back) is served from cache instead of refetching.
const STALE_TIME = 10 * 60 * 1000

// In-flight request; aborted when a newer fetch for a *different* cache key starts, so stale
// responses never overwrite results (react-query only dedupes identical keys). An identical-key
// repeat must NOT abort: react-query would dedupe the new fetchQuery onto the already-aborted
// promise, both cancellation handlers would bail out, and isLoading would stay true forever.
let abortController = null
let inFlightCacheKey = null

// Mirrors ListingPage.jsx: fetch the base listing path (`/3d-models.json`) with the category slugs
// as params rather than `<location.pathname>.json` - the software (/3d-models/3ds-max) and
// new_url_structure (/3d-models/cars/obj) paths would be re-parsed as category slugs there.
// The controller derives the print scope from the path segment (current_print_product_type?),
// so the base follows the asset type: 3D-Print-Only fetches the print listing, and dropping it
// on a print listing falls back to the models listing, exactly like the legacy print toggle.
// The free listings swap between their own pair: /free-3d-print-models carries a print_ready
// route default the controller would keep applying, while /free-3d-models keeps the free scope.
const resolveListingBasePath = (assetType) => {
  const locale = getLocaleParam() || 'en'
  const currentBase = getListingBasePath()
  const printOnly = assetType === ASSET_TYPE.THREE_D_PRINT_ONLY
  const basePairs = [
    [getModelsBasePath(locale), getPrintBasePath(locale)],
    [getFreeModelsBasePath(locale), getFreePrintBasePath(locale)],
  ]

  const pair = basePairs.find(([modelsBase, printBase]) => (
    currentBase === (printOnly ? modelsBase : printBase)
  ))
  if (!pair) return currentBase

  const [modelsBase, printBase] = pair
  return printOnly ? printBase : modelsBase
}

const getJsonUrl = (basePath) => {
  const locale = getLocaleParam()
  return `${locale ? `/${locale}` : ''}/${basePath}.json`
}

// GenerateListingPageUrl returns English, locale-less paths (/3d-models?...), so pushing the
// backend URL verbatim on /es/3d-modelos would drop the /es prefix - and the next getJsonUrl()
// call would then fetch the default-locale endpoint. Re-apply the locale prefix exactly like
// ListingPage.jsx's applyLocalePrefix does (after localizeBackendUrl translates the segments).
const applyLocalePrefix = (url = '') => {
  const locale = getLocaleParam()
  if (!locale) {
    return url
  }

  const normalizedPath = url.startsWith('/') ? url : `/${url}`
  const alreadyPrefixed = normalizedPath === `/${locale}` ||
    normalizedPath.startsWith(`/${locale}/`) ||
    normalizedPath.startsWith(`/${locale}?`)

  return alreadyPrefixed ? normalizedPath : `/${locale}${normalizedPath}`
}

// Search context that lives in the URL query, not the store: the search term (?keywords=...) and
// the visual-search anchors (?image_id= from image search, ?similar_model_id= from "Find similar";
// both read by Searchkick::Items::KnnOptions). They must be re-read on every fetch - otherwise
// paging/filtering would drop them and swap the visual-search results for the plain catalog. The
// backend echoes them into the canonical URL, so they survive the history pushes and a popstate
// restore. Safe to read directly: fetchResults only ever runs client-side.
const URL_SEARCH_CONTEXT_PARAMS = ['keywords', 'image_id', 'similar_model_id']

const getUrlSearchContext = () => {
  if (typeof window === 'undefined') {
    return {}
  }
  const urlParams = new URLSearchParams(window.location.search)
  return Object.fromEntries(
    URL_SEARCH_CONTEXT_PARAMS.map((param) => [param, urlParams.get(param)]).filter(([, value]) => value)
  )
}

// Stable cache key: `current_path` is already part of the url, empty arrays and undefined are
// dropped, so equivalent filter combos map to the same react-query entry (mirrors ListingPage).
const getCacheKeyParams = (params) => {
  const { current_path: _currentPath, ...rest } = params
  return Object.fromEntries(
    Object.entries(rest).filter(
      ([, value]) => value !== undefined && !(Array.isArray(value) && value.length === 0)
    )
  )
}

/**
 * Fetches search results for the current store filters and writes them back to the store.
 * Resets to page 1 unless an explicit `page` is given (pagination). Safe to call repeatedly -
 * the previous request is aborted, and identical filter combos are served from the react-query
 * cache.
 *
 * @param {Object} [options]
 * @param {number} [options.page] - 1-based page; omitted/falsy resets to page 1 (so a plain
 *   perPage change from the UI restarts pagination)
 * @param {number} [options.perPage] - results per page. Defaults to the perPage from the last
 *   response so paging preserves the user's selection.
 * @param {boolean} [options.skipUrlUpdate] - do not push the canonical URL to history; used by
 *   the popstate handler, where the browser already restored the URL and a push would bury the
 *   forward entry.
 * @returns {Promise<void>}
 */
export const fetchResults = ({ page, perPage, skipUrlUpdate = false } = {}) => {
  const state = searchPageStore.getState()
  const targetPage = page || 1
  const targetPerPage = perPage || state.meta?.perPage || DEFAULT_PER_PAGE
  const params = buildSearchParams(state)
  const basePath = resolveListingBasePath(state.assetType)
  // The controller derives the canonical URL's first segment from this, not from the request
  // path, which carries the .json suffix (ListingPage.jsx sends the same param).
  params.current_path = basePath
  if (state.parentCategorySlug) params.parent_category_slug = state.parentCategorySlug
  if (state.subcategorySlug) params.subcategory_slug = state.subcategorySlug
  Object.assign(params, getUrlSearchContext())
  if (targetPage > 1) {
    params.page = targetPage
  }
  if (targetPerPage !== DEFAULT_PER_PAGE) {
    params.per_page = targetPerPage
  }

  const url = getJsonUrl(basePath)
  const queryKey = ['searchPage', url, getCacheKeyParams(params)]
  const cacheKey = JSON.stringify(queryKey)

  if (abortController && cacheKey !== inFlightCacheKey) {
    abortController.abort()
    abortController = null
  }
  if (!abortController) {
    abortController = new AbortController()
  }
  inFlightCacheKey = cacheKey
  const { signal } = abortController

  searchPageStore.setState({ isLoading: true, fetchError: null, page: targetPage })

  return queryClient
    .fetchQuery({
      queryKey,
      queryFn: () => axios.get(url, { params, signal }),
      staleTime: STALE_TIME,
      // Fail fast on a search error (show the error/empty state) instead of the shared client's
      // default retries; the manual abort above already guards against stale overwrites.
      retry: false,
    })
    .then(({ data }) => {
      const {
        data: results = [],
        meta = {},
        filtersOptions = {},
        tags = [],
        url: newUrl,
        pageTitle,
      } = data || {}

      const targetUrl = newUrl ? applyLocalePrefix(localizeBackendUrl(newUrl, getLocaleParam() || 'en')) : ''

      searchPageStore.setState({
        results,
        meta: normalizeMeta(meta),
        filtersOptions,
        resultTags: tags,
        isLoading: false,
        ...(targetUrl ? { canonicalUrl: targetUrl } : {}),
      })

      // Without this the tab keeps the title the server rendered - paging from page 2 back to
      // page 1 would still read "Page 2 of ...". The server owns the localized prefix format
      // (browser.title.page_prefix), so adopt its value instead of rebuilding it here.
      if (pageTitle && typeof document !== 'undefined') {
        document.title = pageTitle
      }

      if (targetUrl && !skipUrlUpdate && typeof window !== 'undefined' && window.history?.pushState) {
        // Deduped identical-key fetches resolve every attached chain; only the first may push, or
        // repeats would stack duplicate history entries for the same URL.
        if (targetUrl !== window.location.pathname + window.location.search) {
          window.history.pushState({}, document.title, targetUrl)
        }
      }
    })
    .catch((error) => {
      if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return
      }
      searchPageStore.setState({ isLoading: false, fetchError: error })
    })
}

// Filter changes can fire in bursts (typing, multi-select); throttle like the legacy listing page.
export const throttledFetchResults = throttle(1000, fetchResults)
